import { useState, useEffect } from "react";
import { MessageCircle, ShieldAlert, Power, CheckCircle, Clock, Search, Edit2, Save, Trash2, X, ChevronDown, ChevronUp, EyeOff, Eye, Download, BarChart2, List, Printer, Lightbulb } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  fetchAllInteractionsFromServer, 
  deleteInteraction, 
  updateInteraction,
  isGlobalInteractionsEnabled,
  isGlobalQaEnabled,
  setGlobalInteractionsEnabled,
  setGlobalQaEnabled,
  getBlockedPartiesForInteractions,
  togglePartyInteractionBlock,
  Interaction 
} from "@/data/interactionsStore";
import { allParties } from "@/data/parties";
import React from "react";
import { getPartyMetrics, PartyMetrics } from "@/data/partyMetricsStore";
import { generateExport } from "@/utils/exportUtils";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell 
} from "recharts";
import { useSuggestionsStore } from "@/data/suggestionsStore";
import { useSearchParams } from "react-router-dom";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#ffc658'];

const AdminInteractionsMessagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [globalEnabled, setGlobalEnabled] = useState(isGlobalInteractionsEnabled());
  const [globalQaEnabled, setGlobalQaEnabled] = useState(isGlobalQaEnabled());
  const [blockedParties, setBlockedParties] = useState<string[]>(getBlockedPartiesForInteractions());
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedParty, setExpandedParty] = useState<string | null>(null);
  const [includeInterceptedInStats, setIncludeInterceptedInStats] = useState(true);
  const [expandedStatsParty, setExpandedStatsParty] = useState<string | null>(null);
  const [statsSelectedParty, setStatsSelectedParty] = useState<string>("ALL");
  const [partyMetrics, setPartyMetrics] = useState<Record<string, PartyMetrics>>({});
  
  // Suggestions logic
  const { suggestions, markAsRead, deleteSuggestion } = useSuggestionsStore();
  const [suggestionFilter, setSuggestionFilter] = useState<string>("ALL");

  // States for editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const loadData = () => {
    setGlobalEnabled(isGlobalInteractionsEnabled());
    setGlobalQaEnabled(isGlobalQaEnabled());
    setBlockedParties(getBlockedPartiesForInteractions());
    setPartyMetrics(getPartyMetrics());
    fetchAllInteractionsFromServer().then(data => {
      setInteractions(data);
    });
  };

  useEffect(() => {
    loadData();
    const handleGlobalUpdate = () => setGlobalEnabled(isGlobalInteractionsEnabled());
    const handleGlobalQaUpdate = () => setGlobalQaEnabled(isGlobalQaEnabled());
    const handlePartyUpdate = () => setBlockedParties(getBlockedPartiesForInteractions());
    
    window.addEventListener("interactionsGlobalSettingsChanged", handleGlobalUpdate);
    window.addEventListener("interactionsGlobalQaSettingsChanged", handleGlobalQaUpdate);
    window.addEventListener("interactionsPartySettingsChanged", handlePartyUpdate);
    return () => {
      window.removeEventListener("interactionsGlobalSettingsChanged", handleGlobalUpdate);
      window.removeEventListener("interactionsGlobalQaSettingsChanged", handleGlobalQaUpdate);
      window.removeEventListener("interactionsPartySettingsChanged", handlePartyUpdate);
    };
  }, []);

  const handleToggleGlobal = () => {
    const next = !globalEnabled;
    setGlobalInteractionsEnabled(next);
    toast({
      title: next ? "Messages activés" : "Messages désactivés",
      description: next ? "Les citoyens peuvent de nouveau écrire aux partis." : "L'envoi de messages directs est bloqué."
    });
  };

  const handleToggleGlobalQa = () => {
    const next = !globalQaEnabled;
    setGlobalQaEnabled(next);
    toast({
      title: next ? "Q&A activé" : "Q&A désactivé",
      description: next ? "La boîte à questions citoyennes est affichée." : "La boîte à questions citoyennes est masquée."
    });
  };

  const handleTogglePartyBlock = (acronym: string) => {
    togglePartyInteractionBlock(acronym);
    toast({
      title: "Statut mis à jour",
      description: `Les interactions pour le parti ${acronym} ont été modifiées.`
    });
  };

  const handleSaveEdit = (id: string) => {
    updateInteraction(id, { message: editContent });
    setEditingId(null);
    loadData();
    toast({
      title: "Message modifié",
      description: "Le texte a été mis à jour dans la base de données."
    });
  };

  const handleToggleIntercept = (id: string, currentIntercepted: boolean) => {
    updateInteraction(id, { isIntercepted: !currentIntercepted });
    loadData();
    toast({
      title: !currentIntercepted ? "Message intercepté" : "Message rétabli",
      description: !currentIntercepted ? "Le message a été masqué pour le parti." : "Le message est de nouveau visible pour le parti.",
      variant: !currentIntercepted ? "destructive" : "default"
    });
  };

  const handleDelete = (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer définitivement ce message ?")) {
      deleteInteraction(id);
      loadData();
      toast({
        title: "Message supprimé",
        description: "Le message a été supprimé de la plateforme.",
        variant: "destructive"
      });
    }
  };

  const handleExport = (format: 'excel' | 'pdf' | 'word') => {
    let interactionsToExport = includeInterceptedInStats 
      ? interactions 
      : interactions.filter(i => !i.isIntercepted);

    if (statsSelectedParty !== "ALL") {
      interactionsToExport = interactionsToExport.filter(i => i.partyAcronym === statsSelectedParty);
    }

    const dataToExport = interactionsToExport.map(i => ({
      "Date": new Date(i.date).toLocaleString('fr-FR'),
      "Parti": i.partyAcronym,
      "Candidat Cible": i.candidateName,
      "Citoyen": `${i.senderFirstName} ${i.senderLastName}`,
      "Contact": `${i.senderEmail} / ${i.senderPhone}`,
      "Région/Province": `${i.region} / ${i.province}`,
      "Statut": i.status,
      "Intercepté": i.isIntercepted ? "Oui" : "Non",
      "Objet": i.subject,
      "Message": i.message
    }));
    generateExport(dataToExport, `Export_Interactions_${statsSelectedParty}_${format}`, `Statistiques des Interactions (${statsSelectedParty})`, format);
  };

  // Group interactions by party
  const interactionsByParty = interactions.reduce((acc, curr) => {
    if (!acc[curr.partyAcronym]) acc[curr.partyAcronym] = [];
    acc[curr.partyAcronym].push(curr);
    return acc;
  }, {} as Record<string, Interaction[]>);

  const filteredParties = allParties.filter(p => 
    p.acronym.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  let filteredInteractionsForStats = includeInterceptedInStats 
    ? interactions 
    : interactions.filter(i => !i.isIntercepted);

  if (statsSelectedParty !== "ALL") {
    filteredInteractionsForStats = filteredInteractionsForStats.filter(i => i.partyAcronym === statsSelectedParty);
  }

  const statsByParty = filteredInteractionsForStats.reduce((acc, curr) => {
    if (!acc[curr.partyAcronym]) acc[curr.partyAcronym] = [];
    acc[curr.partyAcronym].push(curr);
    return acc;
  }, {} as Record<string, Interaction[]>);

  let chartData: any[] = [];
  if (statsSelectedParty === "ALL") {
    chartData = Object.keys(statsByParty).map(acronym => ({
      name: acronym,
      total: statsByParty[acronym].length,
      intercepted: statsByParty[acronym].filter(i => i.isIntercepted).length
    })).sort((a, b) => b.total - a.total);
  } else {
    // Group by region for the specific party
    const statsByRegion = filteredInteractionsForStats.reduce((acc, curr) => {
      const reg = curr.region || "Non Renseignée";
      if (!acc[reg]) acc[reg] = [];
      acc[reg].push(curr);
      return acc;
    }, {} as Record<string, Interaction[]>);
    chartData = Object.keys(statsByRegion).map(region => ({
      name: region,
      total: statsByRegion[region].length,
      intercepted: statsByRegion[region].filter(i => i.isIntercepted).length
    })).sort((a, b) => b.total - a.total);
  }

  const statusData = [
    { name: 'Nouveau', value: filteredInteractionsForStats.filter(i => i.status === 'nouveau').length },
    { name: 'Lu/Traité', value: filteredInteractionsForStats.filter(i => i.status !== 'nouveau').length },
  ];
  if (includeInterceptedInStats) {
    statusData.push({ name: 'Intercepté', value: filteredInteractionsForStats.filter(i => i.isIntercepted).length });
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-foreground flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-primary" />
            Interactions & Messages
          </h1>
          <p className="text-muted-foreground mt-2 font-body">
            Gérez, modérez et contrôlez la messagerie entre les citoyens et les partis politiques.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className={`border-2 transition-all ${globalEnabled ? 'border-primary/20' : 'border-destructive/30 bg-destructive/5'}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full shrink-0 ${globalEnabled ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                  <Power className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Messages Privés</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {globalEnabled 
                      ? "Le bouton 'Intéragir' est visible (sauf blocages individuels)." 
                      : "Désactivé. Les citoyens ne peuvent plus envoyer de messages directs."}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button 
                size="sm" 
                variant={globalEnabled ? "destructive" : "default"} 
                onClick={handleToggleGlobal}
                className="font-bold shadow-md"
              >
                {globalEnabled ? "Désactiver" : "Activer"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 transition-all ${globalQaEnabled ? 'border-primary/20' : 'border-destructive/30 bg-destructive/5'}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full shrink-0 ${globalQaEnabled ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                  <Power className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Questions Publiques (Q&A)</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {globalQaEnabled 
                      ? "La boîte à questions publiques est affichée (sauf blocages)." 
                      : "Désactivée. Les questions publiques sont masquées."}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button 
                size="sm" 
                variant={globalQaEnabled ? "destructive" : "default"} 
                onClick={handleToggleGlobalQa}
                className="font-bold shadow-md"
              >
                {globalQaEnabled ? "Désactiver" : "Activer"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={searchParams.get("tab") || "moderation"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="moderation" className="gap-2"><List className="w-4 h-4"/> Modération des Messages</TabsTrigger>
          <TabsTrigger value="public_qa" className="gap-2"><MessageCircle className="w-4 h-4"/> Questions Publiques (Q&A)</TabsTrigger>
          <TabsTrigger value="propositions" className="gap-2"><Lightbulb className="w-4 h-4"/> Propositions Partenaires</TabsTrigger>
          <TabsTrigger value="stats" className="gap-2"><BarChart2 className="w-4 h-4"/> Statistiques & Exports</TabsTrigger>
        </TabsList>

        <TabsContent value="moderation">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
            <h2 className="text-xl font-display font-bold">Gestion par Parti Politique</h2>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input 
                className="w-full h-10 pl-9 pr-4 rounded-full border border-input bg-background text-sm focus:ring-2 focus:ring-primary/20 transition-shadow" 
                placeholder="Rechercher un parti..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4">
            {filteredParties.map(party => {
              const partyInteractions = interactionsByParty[party.acronym] || [];
              const isBlocked = blockedParties.includes(party.acronym);
              const isExpanded = expandedParty === party.acronym;

              return (
                <Card key={party.acronym} className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'ring-2 ring-primary/20 shadow-md' : 'hover:border-primary/40'}`}>
                  <div 
                    className={`p-4 flex flex-col md:flex-row items-center gap-4 cursor-pointer ${isExpanded ? 'bg-primary/5' : ''}`}
                    onClick={() => setExpandedParty(isExpanded ? null : party.acronym)}
                  >
                    <div className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center bg-white shadow-sm border border-border p-1">
                      {party.logoUrl ? (
                        <img src={party.logoUrl} alt={party.acronym} className="w-full h-full object-contain" />
                      ) : (
                        <span className="font-bold text-sm">{party.acronym}</span>
                      )}
                    </div>
                    
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-bold text-lg">{party.acronym} - {party.name}</h3>
                      <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                        <Badge variant="outline" className="bg-background">
                          {partyInteractions.length} message(s)
                        </Badge>
                        {partyInteractions.filter(i => i.isIntercepted).length > 0 && (
                          <Badge className="bg-orange-100 text-orange-700 border-transparent">
                            {partyInteractions.filter(i => i.isIntercepted).length} intercepté(s)
                          </Badge>
                        )}
                        {isBlocked ? (
                          <Badge className="bg-destructive/10 text-destructive border-transparent">Bloqué par l'admin</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-transparent">Actif</Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant={isBlocked ? "default" : "destructive"} 
                        size="sm"
                        className={isBlocked ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        onClick={() => handleTogglePartyBlock(party.acronym)}
                      >
                        {isBlocked ? "Débloquer le parti" : "Bloquer les interactions"}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setExpandedParty(isExpanded ? null : party.acronym)}>
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4 bg-muted/10 border-t border-border">
                      {partyInteractions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          Aucun message n'a été envoyé à ce parti.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {partyInteractions.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(msg => (
                            <div key={msg.id} className={`bg-card border rounded-xl p-4 shadow-sm relative group ${msg.isIntercepted ? 'border-orange-300 bg-orange-50' : 'border-border'}`}>
                              <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-4 mb-3 pb-3 border-b border-border/50">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="secondary">{new Date(msg.date).toLocaleString('fr-FR')}</Badge>
                                    <span className="text-xs font-semibold text-primary px-2 py-0.5 bg-primary/10 rounded-md">
                                      Pour: {msg.candidateName}
                                    </span>
                                    {msg.isIntercepted && (
                                      <span className="flex items-center gap-1 text-xs font-bold text-orange-600 px-2 py-0.5 bg-orange-100 rounded-md">
                                        <EyeOff className="w-3 h-3" /> Intercepté (Masqué au parti)
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-bold text-sm">
                                    De: {msg.senderFirstName} {msg.senderLastName} <span className="font-normal text-muted-foreground">({msg.senderEmail} / {msg.senderPhone})</span>
                                  </p>
                                  <p className="text-sm font-semibold mt-1">Objet: {msg.subject}</p>
                                </div>
                                
                                <div className="flex flex-wrap gap-2">
                                  {editingId === msg.id ? (
                                    <>
                                      <Button size="sm" onClick={() => handleSaveEdit(msg.id)} className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700">
                                        <Save className="w-3.5 h-3.5" /> Enregistrer
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8">
                                        Annuler
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => { setEditingId(msg.id); setEditContent(msg.message); }} className="h-8 gap-1">
                                        <Edit2 className="w-3.5 h-3.5" /> Modifier
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant={msg.isIntercepted ? "default" : "secondary"} 
                                        onClick={() => handleToggleIntercept(msg.id, !!msg.isIntercepted)} 
                                        className={`h-8 gap-1 ${msg.isIntercepted ? 'bg-orange-500 hover:bg-orange-600' : 'text-orange-600 hover:bg-orange-100'}`}
                                      >
                                        {msg.isIntercepted ? <><Eye className="w-3.5 h-3.5" /> Rétablir</> : <><EyeOff className="w-3.5 h-3.5" /> Intercepter</>}
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => handleDelete(msg.id)} className="h-8 text-destructive hover:bg-destructive/10 px-2">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              <div className="text-sm font-body">
                                {editingId === msg.id ? (
                                  <textarea 
                                    className="w-full min-h-[100px] p-3 rounded-md border border-primary focus:ring-1 focus:ring-primary bg-background"
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                  />
                                ) : (
                                  <p className="whitespace-pre-wrap bg-muted/30 p-3 rounded-md">{msg.message}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="public_qa">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
            <h2 className="text-xl font-display font-bold">Modération des Questions Publiques</h2>
          </div>
          <div className="grid gap-4">
            {interactions.filter(i => i.type === 'public_question').length === 0 ? (
              <div className="text-center py-8 text-muted-foreground bg-muted/10 rounded-xl border border-dashed">
                Aucune question publique soumise.
              </div>
            ) : (
              interactions.filter(i => i.type === 'public_question').map(qa => (
                <Card key={qa.id} className={`border-l-4 ${qa.status === 'approved' ? 'border-l-emerald-500' : qa.status === 'rejected' ? 'border-l-destructive' : 'border-l-primary'} shadow-sm`}>
                  <CardContent className="p-4 flex flex-col md:flex-row gap-4 justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge>{qa.partyAcronym}</Badge>
                        <span className="font-bold">{qa.senderFirstName} {qa.senderLastName} ({qa.senderEmail})</span>
                        <span className="text-xs text-muted-foreground">{new Date(qa.date).toLocaleString('fr-FR')}</span>
                      </div>
                      <p className="text-sm font-body bg-muted/30 p-3 rounded-md">« {qa.message} »</p>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm font-semibold">Statut :</span>
                        {qa.status === 'approved' && <Badge className="bg-emerald-100 text-emerald-700">Approuvée</Badge>}
                        {qa.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejetée</Badge>}
                        {qa.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">En attente de modération</Badge>}
                      </div>
                    </div>
                    
                    <div className="flex flex-row md:flex-col gap-2 shrink-0">
                      <Button size="sm" onClick={() => {
                        updateInteraction(qa.id, { status: 'approved' });
                        loadData();
                        toast({ title: "Approuvée", description: "La question est désormais visible par le parti." });
                      }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                        <CheckCircle className="w-4 h-4" /> Approuver
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => {
                        updateInteraction(qa.id, { status: 'rejected' });
                        loadData();
                      }} className="gap-2">
                        <X className="w-4 h-4" /> Rejeter
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(qa.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4" /> Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="stats">
          <Card id="stats-print-area">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden">
              <div>
                <CardTitle>Vue d'ensemble et Exports</CardTitle>
                <CardDescription>Exportez les données et analysez l'activité d'interaction par parti.</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-full border border-border">
                  <select 
                    value={statsSelectedParty}
                    onChange={(e) => setStatsSelectedParty(e.target.value)}
                    className="bg-transparent border-none text-sm font-bold focus:ring-0 cursor-pointer"
                  >
                    <option value="ALL">Global (Tous les partis)</option>
                    {Object.keys(interactionsByParty).map(acronym => (
                      <option key={acronym} value={acronym}>{acronym}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-full border border-border">
                  <input 
                    type="checkbox" 
                    id="includeIntercepted" 
                    checked={includeInterceptedInStats}
                    onChange={(e) => setIncludeInterceptedInStats(e.target.checked)}
                    className="rounded border-input text-primary focus:ring-primary w-4 h-4"
                  />
                  <label htmlFor="includeIntercepted" className="text-sm font-medium cursor-pointer select-none">
                    Inclure les messages interceptés
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-2 text-slate-700 border-slate-200 hover:bg-slate-50">
                    <Printer className="w-4 h-4" /> Imprimer le Rapport
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('excel')} className="gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                    <Download className="w-4 h-4" /> Tableau Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="hidden print:block mb-8 text-center">
                <h1 className="text-2xl font-bold">Rapport des Interactions Citoyennes - Kayn'in</h1>
                <p className="text-muted-foreground">{statsSelectedParty === "ALL" ? "Tous les partis" : `Parti : ${statsSelectedParty}`}</p>
                <p className="text-sm text-muted-foreground mt-1">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Bar Chart */}
                <div className="h-[300px] w-full">
                  <h3 className="text-sm font-bold text-center mb-4">Volume de messages par {statsSelectedParty === "ALL" ? "Parti" : "Région"}</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{fontSize: 12}} />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="total" name="Total Messages" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      {includeInterceptedInStats && (
                        <Bar dataKey="intercepted" name="Interceptés" fill="#f97316" radius={[4, 4, 0, 0]} />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Pie Chart */}
                <div className="h-[300px] w-full">
                  <h3 className="text-sm font-bold text-center mb-4">Statut Global des Messages</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({name, value}) => `${name}: ${value}`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="overflow-x-auto border border-border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted text-muted-foreground uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 w-10"></th>
                      <th className="px-4 py-3">Parti</th>
                      <th className="px-4 py-3 text-center">Total Messages</th>
                      {includeInterceptedInStats && (
                        <>
                          <th className="px-4 py-3 text-center">Interceptés</th>
                          <th className="px-4 py-3 text-center">Taux d'interception</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(statsByParty)
                      .filter(acronym => statsSelectedParty === "ALL" || acronym === statsSelectedParty)
                      .map(acronym => {
                      const partyData = statsByParty[acronym];
                      const total = partyData.length;
                      if (total === 0) return null;
                      
                      const intercepted = partyData.filter(i => i.isIntercepted).length;
                      const rate = total > 0 ? ((intercepted / total) * 100).toFixed(1) : '0.0';
                      const isExpanded = statsSelectedParty === acronym || expandedStatsParty === acronym;
                      
                      // Grouper par région puis province
                      const byRegion = partyData.reduce((acc, msg) => {
                        if (!acc[msg.region]) acc[msg.region] = {};
                        if (!acc[msg.region][msg.province]) acc[msg.region][msg.province] = 0;
                        acc[msg.region][msg.province]++;
                        return acc;
                      }, {} as Record<string, Record<string, number>>);
                      
                      return (
                        <React.Fragment key={acronym}>
                          <tr 
                            className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
                            onClick={() => setExpandedStatsParty(isExpanded ? null : acronym)}
                          >
                            <td className="px-4 py-3 text-center">
                              {isExpanded ? <ChevronUp className="w-4 h-4 mx-auto" /> : <ChevronDown className="w-4 h-4 mx-auto" />}
                            </td>
                            <td className="px-4 py-3 font-semibold">{acronym}</td>
                            <td className="px-4 py-3 text-center">{total}</td>
                            {includeInterceptedInStats && (
                              <>
                                <td className="px-4 py-3 text-center">
                                  {intercepted > 0 ? <span className="text-orange-600 font-bold">{intercepted}</span> : "0"}
                                </td>
                                <td className="px-4 py-3 text-center text-muted-foreground">{rate}%</td>
                              </>
                            )}
                          </tr>
                          {isExpanded && (
                            <tr className="bg-muted/10 border-b border-border/50">
                              <td colSpan={includeInterceptedInStats ? 5 : 3} className="p-0">
                                <div className="p-4 pl-12">
                                  <h4 className="font-semibold text-xs text-muted-foreground uppercase mb-2">Détails par Région et Province</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(byRegion).map(([region, provinces]) => (
                                      <div key={region} className="bg-background border border-border rounded-lg p-3">
                                        <div className="font-bold text-sm text-primary mb-2 border-b pb-1">{region || "Région Non Renseignée"}</div>
                                        <ul className="space-y-1 mt-2">
                                          {Object.entries(provinces).map(([province, count]) => (
                                            <li key={province} className="flex justify-between items-center text-xs">
                                              <span className="text-muted-foreground">{province || "Non renseignée"}</span>
                                              <span className="font-semibold bg-muted px-1.5 py-0.5 rounded">{count as number} msg</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          <Card className="mt-8 mb-8 break-inside-avoid">
            <CardHeader>
              <CardTitle>Métriques d'Engagement (Clics et Likes)</CardTitle>
              <CardDescription>Vue détaillée des interactions des citoyens avec les éléments de profil des partis.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border border-border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3">Parti</th>
                      <th className="px-4 py-3 text-center">Présentation</th>
                      <th className="px-4 py-3 text-center">Programme</th>
                      <th className="px-4 py-3 text-center">Site Web</th>
                      <th className="px-4 py-3 text-center">Facebook</th>
                      <th className="px-4 py-3 text-center">X</th>
                      <th className="px-4 py-3 text-center">Instagram</th>
                      <th className="px-4 py-3 text-center">YouTube</th>
                      <th className="px-4 py-3 text-center text-pink-600">Likes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(partyMetrics)
                      .filter(acronym => statsSelectedParty === "ALL" || acronym === statsSelectedParty)
                      .map(acronym => {
                        const m = partyMetrics[acronym];
                        return (
                          <tr key={acronym} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="px-4 py-3 font-semibold">{acronym}</td>
                            <td className="px-4 py-3 text-center font-mono">{m.presentation || 0}</td>
                            <td className="px-4 py-3 text-center font-mono">{m.program || 0}</td>
                            <td className="px-4 py-3 text-center font-mono">{m.website || 0}</td>
                            <td className="px-4 py-3 text-center font-mono text-blue-600">{m.facebook || 0}</td>
                            <td className="px-4 py-3 text-center font-mono text-sky-500">{m.twitter || 0}</td>
                            <td className="px-4 py-3 text-center font-mono text-pink-600">{m.instagram || 0}</td>
                            <td className="px-4 py-3 text-center font-mono text-red-600">{m.youtube || 0}</td>
                            <td className="px-4 py-3 text-center font-mono font-bold text-pink-600">{m.like || 0}</td>
                          </tr>
                        );
                      })}
                      {Object.keys(partyMetrics).filter(acronym => statsSelectedParty === "ALL" || acronym === statsSelectedParty).length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                            Aucune donnée d'engagement n'a été enregistrée pour le moment.
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="propositions">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
            <h2 className="text-xl font-display font-bold">Propositions des partenaires</h2>
            <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-full border border-border">
              <select 
                value={suggestionFilter}
                onChange={(e) => setSuggestionFilter(e.target.value)}
                className="bg-transparent border-none text-sm font-bold focus:ring-0 cursor-pointer"
              >
                <option value="ALL">Tous les partenaires</option>
                <option value="parti">Partis Politiques</option>
                <option value="media">Médias</option>
                <option value="ong">ONGs & Associations</option>
                <option value="institution">Institutions</option>
                <option value="sap">Sans Appartenance Politique (SAP)</option>
              </select>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-primary">
                Nouvelles propositions ({suggestions.filter(s => s.status === 'unread' && (suggestionFilter === 'ALL' || s.partnerType === suggestionFilter)).length})
              </h3>
              <div className="grid gap-4">
                {suggestions.filter(s => s.status === 'unread' && (suggestionFilter === 'ALL' || s.partnerType === suggestionFilter)).map(s => (
                  <Card key={s.id} className="border-l-4 border-l-primary shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4 justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge>{s.partnerType.toUpperCase()}</Badge>
                          <span className="font-bold">{s.partnerName}</span>
                          <span className="text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleString('fr-FR')}</span>
                        </div>
                        <h4 className="font-bold text-md">{s.subject}</h4>
                        <p className="text-sm text-foreground bg-muted/30 p-3 rounded-md whitespace-pre-wrap">{s.text}</p>
                      </div>
                      <div className="flex flex-row md:flex-col gap-2 shrink-0">
                        <Button size="sm" onClick={() => markAsRead(s.id)} className="gap-2 bg-primary hover:bg-primary/90">
                          <CheckCircle className="w-4 h-4" /> Marquer Lu
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {suggestions.filter(s => s.status === 'unread' && (suggestionFilter === 'ALL' || s.partnerType === suggestionFilter)).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground bg-muted/10 rounded-xl border border-dashed">Aucune nouvelle proposition.</div>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-muted-foreground">
                <Clock className="w-5 h-5" /> Historique des propositions ({suggestions.filter(s => s.status === 'read' && (suggestionFilter === 'ALL' || s.partnerType === suggestionFilter)).length})
              </h3>
              <div className="grid gap-4 opacity-80">
                {suggestions.filter(s => s.status === 'read' && (suggestionFilter === 'ALL' || s.partnerType === suggestionFilter)).map(s => (
                  <Card key={s.id} className="border-border shadow-none bg-muted/10">
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4 justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{s.partnerType.toUpperCase()}</Badge>
                          <span className="font-bold text-muted-foreground">{s.partnerName}</span>
                          <span className="text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleString('fr-FR')}</span>
                        </div>
                        <h4 className="font-bold text-md text-muted-foreground">{s.subject}</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{s.text}</p>
                      </div>
                      <div className="flex flex-row md:flex-col gap-2 shrink-0 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => deleteSuggestion(s.id)} className="text-destructive hover:bg-destructive/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInteractionsMessagesPage;
